import { colors } from '../styles/tokens';
import { fmtInt, fmtMoney, fmtPct, mmss } from './format';
import { esLunes, etiquetaSemanaDiseno, lunesDe, partesSemana } from '../../metas-colaborador/utils/semanas';
import type { Bloque, KpiScorecard, Medicion, Polaridad, Unidad } from '../types/scorecard.types';

/**
 * Utilidades PURAS de la pestaña Scorecard (spec 2026-10-16-scorecard-de-tienda).
 *
 * El front no recalcula cocientes: la API manda real, objetivo, cumplimiento,
 * año anterior, variación y lugar. Aquí sólo se formatea y se elige el color,
 * con la MISMA polaridad que usó la API para rankear.
 */

/** Un valor en su unidad: monto → $, pct → %, segundos → mm:ss. `null` → «—». */
export function fmtValor(v: number | null | undefined, unidad: Unidad): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  switch (unidad) {
    case 'monto':
      return fmtMoney(v);
    case 'pct':
      return fmtPct(v);
    case 'segundos':
      return mmss(v);
    case 'decimal':
      return v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case 'meses':
      return `${v.toLocaleString('es-MX', { maximumFractionDigits: 1 })} meses`;
    default:
      return fmtInt(v);
  }
}

/**
 * Diferencia contra el objetivo con signo. En un KPI de porcentaje se dice en
 * puntos porcentuales («+2,3 pp»): «+2,3 %» se leería como crecimiento relativo.
 */
export function fmtDiferencia(v: number | null | undefined, unidad: Unidad): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const signo = v > 0 ? '+' : v < 0 ? '−' : '';
  if (unidad === 'pct') {
    const pp = Math.round(Math.abs(v) * 1000) / 10;
    return `${signo}${pp.toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} pp`;
  }
  return signo + fmtValor(Math.abs(v), unidad);
}

/** Variación contra el año anterior: «▲ 3.1 %» / «▼ 2.0 %». */
export function fmtVariacion(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const flecha = v > 0 ? '▲' : v < 0 ? '▼' : '=';
  return `${flecha} ${fmtPct(Math.abs(v))}`;
}

/**
 * Color del cumplimiento contra el objetivo (fracción). Verde desde 100 %,
 * dorado de 90 a 100, rojo debajo; invertido si menos es mejor.
 */
export function colorCumplimiento(c: number | null | undefined, polaridad: Polaridad): string {
  if (c === null || c === undefined || polaridad === 'neutro') return colors.texto.tertiary;
  const bueno = polaridad === 'menor_mejor' ? c <= 1 : c >= 1;
  const cerca = polaridad === 'menor_mejor' ? c <= 1.1 : c >= 0.9;
  return bueno ? colors.verde : cerca ? colors.dorado : colors.rojo;
}

/** Color de la variación contra AA, orientado por la polaridad; gris si es neutro. */
export function colorVariacion(v: number | null | undefined, polaridad: Polaridad): string {
  if (v === null || v === undefined || polaridad === 'neutro' || Math.abs(v) < 0.0005) return colors.texto.tertiary;
  const mejora = polaridad === 'menor_mejor' ? v < 0 : v > 0;
  return mejora ? colors.verde : colors.rojo;
}

/** El KPI no lleva semáforo: está en validación, no aplica o no tiene dato. */
export const sinSemaforo = (k: Pick<KpiScorecard, 'estado'>): boolean => k.estado !== 'ok';

export const ROTULO_BLOQUE: Record<Bloque, string> = {
  resultados: 'Resultados',
  productividad: 'Productividad',
  cultura: 'Cultura',
  operacion: 'Operación',
};

export const ORDEN_BLOQUES: Bloque[] = ['resultados', 'productividad', 'cultura', 'operacion'];

/** KPIs de un bloque, en el orden en que los manda la API. */
export const kpisDelBloque = (kpis: KpiScorecard[], bloque: Bloque): KpiScorecard[] =>
  kpis.filter((k) => k.bloque === bloque);

/** ¿Algún KPI del bloque tiene objetivo en la vista elegida? Si no, la tabla quita esas columnas. */
export const bloqueConObjetivo = (kpis: KpiScorecard[], ver: 'semana' | 'acumulado'): boolean =>
  kpis.some((k) => k[ver].objetivo !== null);

/** La medición de la vista elegida. */
export const medicionDe = (k: KpiScorecard, ver: 'semana' | 'acumulado'): Medicion => k[ver];

/** «Semana 41 · 5 – 11 oct 2026» (la etiqueta del diseño de Metas por colaborador). */
export const textoSemana = etiquetaSemanaDiseno;

/** «Sem. 41 · 5 – 11 oct», para el teléfono. */
export function textoSemanaCorto(lunes: string): string {
  const p = partesSemana(lunes);
  return `Sem. ${p.numero} · ${p.rangoCorto}`;
}

/** Lugar «3 de 80» o «—». */
export const fmtLugar = (l: { lugar: number; de: number } | null | undefined): string =>
  l ? `${l.lugar} de ${l.de}` : '—';

/** Movimiento contra la semana anterior: «▲2», «▼1», «=», o vacío sin dato. */
export function movimiento(actual: number | null | undefined, anterior: number | null | undefined): { texto: string; color: string } {
  if (!actual || !anterior) return { texto: '', color: colors.texto.tertiary };
  const d = anterior - actual; // subir de lugar es bajar el número
  if (d === 0) return { texto: '=', color: colors.texto.tertiary };
  return d > 0 ? { texto: `▲${d}`, color: colors.verde } : { texto: `▼${-d}`, color: colors.rojo };
}

// ─────────────────────────────── URL ───────────────────────────────

export interface ScorecardUrl {
  semana: string | null;
  cadena: string | null;
  tienda: number | null;
  sub: 'tienda' | 'ranking';
  ver: 'semana' | 'acumulado';
}

/**
 * Lo que pide la URL: `/indicadores?vista=scorecard&semana=…&cadena=VAZ&tienda=56[&sub=ranking][&ver=acumulado]`.
 * Se lee de `window.location` y no del router (las pruebas montan la pantalla
 * sin router, como con Presupuesto). Una semana que no cae en lunes se lleva a
 * su lunes; basura → `null`. Nunca lanza.
 */
export function leerScorecardDeUrl(): (ScorecardUrl & { pide: boolean }) | null {
  try {
    const q = new URLSearchParams(window.location.search);
    const s = q.get('semana');
    const semana = s && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) ? (esLunes(s) ? s : lunesDe(s)) : null;
    const t = Number(q.get('tienda'));
    return {
      pide: q.get('vista') === 'scorecard',
      semana,
      cadena: (q.get('cadena') || '').toUpperCase() || null,
      tienda: Number.isInteger(t) && t > 0 ? t : null,
      sub: q.get('sub') === 'ranking' ? 'ranking' : 'tienda',
      ver: q.get('ver') === 'acumulado' ? 'acumulado' : 'semana',
    };
  } catch {
    return null;
  }
}

/**
 * Escribe el estado en la URL para poder compartir el enlace. Conserva
 * `history.state`: React Router guarda ahí su llave, y pisarlo rompe «atrás».
 * `null` quita los parámetros del scorecard (al salir de la pestaña).
 */
export function escribirScorecardEnUrl(estado: ScorecardUrl | null): void {
  try {
    const url = new URL(window.location.href);
    for (const k of ['vista', 'semana', 'cadena', 'tienda', 'sub', 'ver']) url.searchParams.delete(k);
    if (estado) {
      url.searchParams.set('vista', 'scorecard');
      if (estado.semana) url.searchParams.set('semana', estado.semana);
      if (estado.cadena) url.searchParams.set('cadena', estado.cadena);
      if (estado.tienda) url.searchParams.set('tienda', String(estado.tienda));
      if (estado.sub === 'ranking') url.searchParams.set('sub', 'ranking');
      if (estado.ver === 'acumulado') url.searchParams.set('ver', 'acumulado');
    }
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch {
    /* sin `window` (pruebas de nodo): nada que escribir */
  }
}
